import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, DataTable } from '../../components/office';
import { purchaseOrdersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Skeleton } from '@verlo/shared/components/ui/skeleton';
import { useAuthoringSite, useSite } from '../../contexts/SiteContext';
import { nextStep, orderStatus, qty, statusNote } from '../../lib/orders';
import { deliveryWords } from '../../lib/delivery';
import ConfirmOrderDialog from './ConfirmOrderDialog';

// Orders (Back office sweep, Oct 2026: D10, mock "Orders"). What you buy from suppliers:
// drafted, sent, and checked in when it arrives. Every way to start one opens the order builder
// (`OrderBuilderPage`, Oct 2026): Order these, filled with what's below par; a bundle you saved;
// or New order, empty. The drafts it makes land here. Once sent, each order says what's next
// (Oct 2026, on its way): mark it confirmed when they reply, chase it when they haven't by
// their cut-off, receive it when it comes.

const money = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const day = (iso) => (iso ? new Date(iso.length > 10 ? iso : `${iso}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '');

export default function OrdersPage() {
  const navigate = useNavigate();
  const { site } = useSite();
  const authoring = useAuthoringSite();
  const [orders, setOrders] = useState(null);
  const [low, setLow] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [open, setOpen] = useState(null);       // the supplier group shown under To order
  const [confirming, setConfirming] = useState(null);  // the order being marked confirmed
  const [chasing, setChasing] = useState(null);
  const drafted = useLocation().state?.drafted || null;   // what the builder just drafted
  const sent = useLocation().state?.sent || null;         // what Review and send just sent
  const inProgress = (() => { try { return Boolean(JSON.parse(localStorage.getItem(`verlo.orderBasket.${authoring.site || site || 'all'}`) || 'null')?.lines?.length); } catch { return false; } })();

  const load = () => Promise.all([
    purchaseOrdersApi.list(site || undefined),
    purchaseOrdersApi.suggestions(site || undefined),
    purchaseOrdersApi.bundles().catch(() => ({ data: [] })),
  ]).then(([o, s, b]) => {
    setOrders(o.data || []);
    setLow(s.data);
    setBundles(b.data || []);
    setOpen((cur) => cur ?? s.data?.groups?.[0]?.supplier_id ?? null);
  }).catch(() => toast.error("Couldn't load the orders"));
  useEffect(() => { load(); }, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  const draftIds = (orders || []).filter((o) => o.status === 'draft').map((o) => o.id);
  const groups = (low?.groups || []).filter((g) => g.supplier_id);
  const noSupplier = (low?.groups || []).find((g) => !g.supplier_id);
  const toOrderCount = (low?.items || 0) - (noSupplier?.lines.length || 0);

  // Not confirmed: email it again, or (no email) say their number to ring.
  const chase = async (o) => {
    setChasing(o.id);
    try {
      await purchaseOrdersApi.chase(o.id);
      toast.success(`${o.number} chased: emailed to ${o.supplier_name} again`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === 'cant_email') toast.error(`${d.message} ${d.phone ? `Ring them on ${d.phone}.` : 'Ring them.'}`);
      else toast.error((typeof d === 'string' && d) || "Couldn't chase it");
    } finally { setChasing(null); }
  };
  const stop = (fn) => (e) => { e.stopPropagation(); fn(); };
  const next = (o) => {
    const step = nextStep(o);
    // A draft on its own: pick which go, one at a time if you like (Martin, 27 Oct).
    if (step === 'send') return <Button size="sm" variant="outline" onClick={stop(() => navigate(`/back-office/orders/send?ids=${o.id}`))} data-testid={`order-send-${o.number}`}>Review and send</Button>;
    if (step === 'confirm') return <Button size="sm" variant="outline" onClick={stop(() => setConfirming(o))} data-testid={`order-confirm-${o.number}`}>Mark confirmed</Button>;
    if (step === 'chase') return (
      <span className="inline-flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={chasing === o.id} onClick={stop(() => chase(o))} data-testid={`order-chase-${o.number}`}>Chase</Button>
        <button type="button" className="text-[12.5px] text-verlo hover:underline" onClick={stop(() => setConfirming(o))}>Mark confirmed</button>
      </span>
    );
    if (step === 'receive') return <Button size="sm" variant="outline" onClick={stop(() => navigate(`/back-office/orders/${o.id}/receive`))} data-testid={`order-receive-${o.number}`}>Receive</Button>;
    return null;
  };

  const columns = [
    { key: 'supplier_name', label: 'Supplier', sortable: true, render: (o) => (
      <div><p className="font-semibold text-ink">{o.supplier_name}</p><p className="font-mono text-[12px] text-ink-faint">{o.number}</p></div>
    ) },
    { key: 'status', label: 'Status', render: (o) => {
      const st = orderStatus(o);
      const note = o.status === 'sent' || o.status === 'draft' ? statusNote(o)
        : `${day(o.received_at)}${o.their_ref ? ` · their ref ${o.their_ref}` : ''}`;
      return (
        <div className="flex flex-wrap items-center gap-x-2">
          <Signal tone={st.tone} dot={false}>{st.words}</Signal>
          {note && <span className="text-[12.5px] text-ink-faint">{note}{o.stage === 'chase' && o.supplier_phone ? ` · ${o.supplier_phone}` : ''}</span>}
        </div>
      );
    } },
    { key: 'deliver_on', label: 'Due', sortable: true, render: (o) => <span className="text-ink-soft">{o.status === 'sent' || o.status === 'draft' ? deliveryWords(o.deliver_on) || '—' : '—'}</span> },
    { key: 'total', label: 'Total', align: 'right', mono: true, sortable: true, render: (o) => money(o.total) },
    { key: 'next', label: '', align: 'right', render: next },
  ];

  return (
    <div className="space-y-5" data-testid="orders-page">
      <PageHeader
        title="Orders"
        purpose="What you buy from suppliers: drafted, sent, and checked in when it arrives."
        actions={(
          <>
            {draftIds.length > 0 && (
              <Button variant="outline" onClick={() => navigate(`/back-office/orders/send?ids=${draftIds.join(',')}`)} data-testid="orders-send-drafts">
                Review and send {draftIds.length} draft{draftIds.length === 1 ? '' : 's'}
              </Button>
            )}
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate('/back-office/orders/new')} disabled={!authoring.ready} title={authoring.reason || undefined} data-testid="order-new">{inProgress ? 'Carry on with your order' : 'New order'}</Button>
          </>
        )}
      />

      {sent && (
        <Notice tone="ok" word="Sent" data-testid="orders-sent">
          {sent.emailed} order{sent.emailed === 1 ? '' : 's'} emailed, {money(sent.total)}{sent.byHand ? `; ${sent.byHand} sent by hand` : ''}{sent.drafts?.length ? `; ${sent.drafts.join(', ')} left as draft${sent.drafts.length === 1 ? '' : 's'}` : ''}.
          {sent.via === 'dev_mailbox' && <> On this development copy they&apos;re in <button type="button" className="font-semibold text-verlo hover:underline" onClick={() => navigate('/back-office/orders/emails')}>Emails sent</button>.</>}
        </Notice>
      )}
      {drafted && (
        <Notice tone="ok" word="Drafted" data-testid="orders-drafted">
          {drafted.made > 0 && `${drafted.made} new draft${drafted.made === 1 ? '' : 's'}`}{drafted.made > 0 && drafted.added.length > 0 && ', and '}
          {drafted.added.length > 0 && `added to ${drafted.added.join(', ')}`}, {money(drafted.total)}. Saved for later: Review and send them when you're ready.
        </Notice>
      )}

      {low === null ? <Skeleton className="h-40" /> : groups.length > 0 && (
        <section className="rounded-lg border border-hairline bg-white" aria-label="To order" data-testid="orders-low">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline px-5 py-4">
            <div>
              <p className="font-heading text-[16px] font-extrabold text-ink">To order: {toOrderCount} item{toOrderCount === 1 ? '' : 's'} below par from {groups.length} supplier{groups.length === 1 ? '' : 's'}</p>
              <p className="text-[13px] text-ink-soft">Bringing them up to par comes to <b className="font-mono font-semibold text-ink">{money(low.total)}</b>, rounded to each supplier's pack size.</p>
            </div>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => navigate('/back-office/orders/new?from=par')} disabled={!authoring.ready} data-testid="orders-order-these">
              Review and order
            </Button>
          </div>
          {groups.map((g) => {
            const shut = open !== g.supplier_id;
            return (
              <div key={g.supplier_id} className="border-b border-hairline last:border-0">
                <button type="button" onClick={() => setOpen(shut ? g.supplier_id : null)} className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-4 px-5 py-2.5 text-left hover:bg-hover-tint" aria-expanded={!shut}>
                  <span className="flex items-center gap-2 font-semibold text-ink">{shut ? <ChevronRight size={15} /> : <ChevronDown size={15} />}{g.supplier_name}
                    {(orders || []).some((o) => o.status === 'draft' && o.supplier_id === g.supplier_id) && <Signal tone="info" dot={false}>has a draft</Signal>}</span>
                  <span className="text-[13px] text-ink-soft">{g.lines.length} item{g.lines.length === 1 ? '' : 's'}</span>
                  <span className="w-20 text-right font-mono tabular-nums text-ink">{money(g.total)}</span>
                </button>
                {!shut && (
                  <table className="mb-2 w-full text-[13.5px]">
                    <thead><tr className="text-left font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">
                      <th className="px-5 py-1.5 pl-12">Item</th><th className="px-3">Have</th><th className="px-3">Par</th><th className="px-3">Order</th><th className="px-5 text-right">Cost</th></tr></thead>
                    <tbody>{g.lines.map((l) => (
                      <tr key={l.stock_id}>
                        <td className="px-5 py-1.5 pl-12 text-ink">{l.name}
                          {l.short_from && <span className="block text-[12px] text-signal-warn">{qty(l.short_from.short, l.unit)} short from {l.short_from.number}</span>}</td>
                        <td className="px-3 font-mono tabular-nums text-ink-soft">{qty(l.have, l.unit)}</td>
                        <td className="px-3 font-mono tabular-nums text-ink-faint">{qty(l.par, l.unit)}</td>
                        <td className="px-3 font-mono tabular-nums text-ink">{qty(l.quantity, l.unit)}{l.pack_size > 1 ? <span className="ml-1 text-[12px] text-ink-faint">({l.quantity / l.pack_size} × {l.pack_size})</span> : null}</td>
                        <td className="px-5 text-right font-mono tabular-nums text-ink">{money(l.quantity * l.price)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            );
          })}
          {noSupplier && (
            <p className="px-5 py-3 text-[12.5px] text-ink-faint">{noSupplier.lines.length} more running low with no supplier set: {noSupplier.lines.slice(0, 4).map((l) => l.name).join(', ')}{noSupplier.lines.length > 4 ? '…' : ''}. Give them one in Stock levels.</p>
          )}
        </section>
      )}

      <section aria-label="Bundles" data-testid="orders-bundles">
        <div className="mb-2 flex items-center gap-2.5">
          <span className="font-heading text-[11px] font-bold uppercase tracking-[0.08em] text-ink-faint">Your bundles</span>
          <button type="button" className="text-[12.5px] font-semibold text-verlo hover:underline" onClick={() => navigate('/back-office/orders/bundles')} data-testid="orders-bundles-manage">Edit</button>
        </div>
        {bundles.length === 0 ? (
          <p className="rounded-lg border border-dashed border-hairline-strong px-4 py-3 text-[13px] text-ink-soft">A basket you order often, saved for one tap. None yet: save one from any order with “Save as a bundle”, or start one with Edit.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {bundles.map((b) => (
              <div key={b.id} className="flex flex-col gap-1 rounded-lg border border-hairline bg-white px-4 py-3" data-testid={`bundle-${b.id}`}>
                <b className="text-[14px] font-semibold text-ink">{b.name}</b>
                <span className="text-[12.5px] text-ink-soft">{b.supplier_name} · {b.lines.length} item{b.lines.length === 1 ? '' : 's'}</span>
                <div className="mt-1 flex items-center justify-between">
                  <button type="button" className="text-[12.5px] text-ink-faint hover:text-signal-stop" onClick={async () => { await purchaseOrdersApi.removeBundle(b.id); load(); }}>Remove</button>
                  <Button size="sm" variant="outline" onClick={() => navigate(`/back-office/orders/new?bundle=${b.id}`)} data-testid={`bundle-order-${b.id}`}>Order</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <DataTable
        columns={columns} rows={orders || []} rowKey="id" loading={orders === null}
        onRowClick={(o) => navigate(`/back-office/orders/${o.id}`)}
        empty={{ word: 'Orders', title: 'No orders yet', children: 'Start one from what’s running low, a bundle, or New order.' }}
        testId="orders-table"
      />

      {confirming && <ConfirmOrderDialog order={confirming} onClose={() => setConfirming(null)} onDone={() => { setConfirming(null); load(); }} />}

    </div>
  );
}
